import _                                              from 'underscore';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Form }                                       from 'semantic-ui-react';
import { Formik }                                     from 'formik';
import * as Yup                                       from 'yup';

import { runIfFunction }                              from '../../../../util';
import { SystemAccountingQueries }                    from '../../../../queries';
import { Align, Size, Uuid }                          from '../../../../types';
import { QueryDialogProps, QueryOpDialog }            from '../../../components/from-query/QueryOpDialog';
import { BasicDialogContainer }                       from '../../../components/modals/BasicDialogContainer';
import { TextField }                                  from '../../../components/forms/formik';
import { ProjectContext }                             from '../ProjectIndexPage';
import { useQuery }                                   from '@apollo/react-hooks';
import {queriesHaveData, queriesLoading, withoutType} from '../../../../store/_apolloUtil';
import { StyledContainer }                            from '../../../containers';
import { LoadingIndicator }                           from '../../../components/util/loading/LoadingIndicator';
import { useCurrentUser }                             from '../../../../hooks';

interface Props extends QueryDialogProps {
	projectId?: Uuid,
	projectName?: string,
}

interface FormValues {
	projectName: string
}

const defaultProps: Props = {
	projectId:   null,
	projectName: null,
}

const RenameProjectDialog: FC<Props> = React.memo<Props>( ( props ) => {

	let {
		    projectId:   currentProjectId = null,
		    projectName: currentProjectName
	    }               = useContext( ProjectContext ),
	    { currentUser } = useCurrentUser();

	const projectId   = currentProjectId ?? props.projectId,
	      projectName = currentProjectName ?? props.projectName ?? '';

	let [ requestedProject, setRequestedProject ] = useState( null ),
	    getProjectReq                             = useQuery( SystemAccountingQueries.queries.GET_PROJECT( SystemAccountingQueries.fragments.PROJECTS.EDIT_FIELDS() ), {
		    variables: { id: projectId, },
		    skip:      !projectId,
	    } );

	let hasRequestedProject = !_.isNull( requestedProject ),
	    isLoadingProject    = !hasRequestedProject || queriesLoading( getProjectReq );

	useEffect( () => {

		if( !queriesLoading( getProjectReq ) && queriesHaveData( getProjectReq ) ){

			setRequestedProject( _.first( getProjectReq.data.projects ) )
		}

	}, [ getProjectReq ] );

	useEffect( () => {}, [ requestedProject ] )

	return (

		<Formik<FormValues> initialValues={ { projectName } }
		                    isInitialValid={ false }
		                    enableReinitialize={ true }
		                    validateOnChange={ true }
		                    validationSchema={ Yup.object( {
			                    projectName: Yup.string().test( 'unique name', 'Project names must be unique', ( value: string ) => {

				                    let existingProjectNames = [ ..._.map( currentUser.projects ?? [], item => item.project.name ) ];

				                    return !existingProjectNames.includes( value?.trim() ?? '' );

			                    } ).required( 'Project name cannot be blank.' )
		                    } ) }
		                    onSubmit={ null }>

			{ formik => (

				isLoadingProject
				? (
					<StyledContainer padding={ Size.LG }>
						<LoadingIndicator dimmer={ false } isActive={ true } label={ 'Loading Project Info...' }/>
					</StyledContainer>
				) : (
					<QueryOpDialog
						{ ...props }
						operationDisabled={ !projectId || isLoadingProject || !formik.isValid }
						onComplete={ () => runIfFunction( props.onComplete, { name: formik.values.projectName } ) }
						mutation={ SystemAccountingQueries.mutations.UPDATE_PROJECT( SystemAccountingQueries.fragments.PROJECTS.EDIT_FIELDS() ) }
						graphArgs={ () => ( {
							variables: {
								input: {
									...withoutType(requestedProject),
									name: formik.values.projectName.trim()
								}
							}
						} ) }
						queryOptions={ {
							onCompleted: ( data ) => {
								getProjectReq.refetch();
							}
						} }
						submitLabel={ 'Update' }
						className={ 'no-padding' }
						alignControls={ Align.CENTER }>


						<Form>

							<BasicDialogContainer title={ 'Rename Project' } roundedTop contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }>

								<TextField
									autoFocus={ true }
									type={ 'text' }
									descriptionText={ 'Choose a unique name that will help your team identify this project’s purpose.' }
									size={ 'big' }
									icon={ 'briefcase' }
									iconPosition='left'
									name={ 'projectName' }
									label={ 'Project Name' }
								/>

							</BasicDialogContainer>
						</Form>

					</QueryOpDialog>
				)
			) }

		</Formik>
	)
} );

RenameProjectDialog.defaultProps = defaultProps;

export { RenameProjectDialog };
